import { mapLocaleToContentLanguage } from 'cf-i18n/constants'
import { useLocale } from 'next-intl'
import { ContentLanguage } from 'codegen/types'
import type { ContentFetchProductQuery } from '@web/gen/content'
import {
  useContentFetchLicenseQuery,
  useContentFetchProductQuery,
  useContentCountFavoritesQuery,
} from '@web/gen/content'
import { useProductId } from '@web/app/[locale]/(web)/prod/[slug]/product-id-context'
import { useLegacyProductsFetchLicenseQuery } from '@web/gen/cf'

export type FullContentProduct = ContentFetchProductQuery['contentProduct'] & {
  favoriteCount?: number
  isDownloaded?: boolean
}

export function useFullContentProductDataQuery(
  productId: string,
  options?: { enabled?: boolean },
): {
  refetch: () => void
  isLoading: boolean
  data: FullContentProduct | null
} {
  const {
    data: productData,
    isPending: productDataLoading,
    refetch: refetchProductData,
  } = useContentProductDataByIdQuery(productId, options)

  const {
    data: favoritesCount,
    isPending: favoritesCountLoading,
    refetch: refetchFavoritesCount,
  } = useContentCountFavoritesQuery({ productId }, options)

  const {
    data: downloadData,
    isPending: downloadDataLoading,
    refetch: refetchLicense,
  } = useContentFetchLicenseQuery(
    {
      input: {
        productID: productId ?? '',
      },
    },
    options,
  )

  if (!productId && options === undefined) {
    throw new Error('Please specify a product id or set enabled property')
  }

  if (!productData) {
    return {
      refetch: () => {
        return
      },
      isLoading: false,
      data: null,
    }
  }

  return {
    refetch: () => {
      refetchProductData()
      refetchFavoritesCount()
      refetchLicense()
    },
    isLoading: productDataLoading || favoritesCountLoading || downloadDataLoading,
    data: {
      ...productData,
      favoriteCount: favoritesCount?.contentCountFavorites?.total,
      isDownloaded: !!downloadData?.contentFetchLicense?.key,
    },
  }
}

export function useContentProductDataByIdQuery(productId: string, options?: { enabled?: boolean }) {
  const locale = useLocale()
  const contentLanguage = mapLocaleToContentLanguage.get(locale) || ContentLanguage.LanguageEn

  if (!productId && options === undefined) {
    throw new Error('Please specify a product id or set enabled property')
  }

  return useContentFetchProductQuery(
    {
      id: productId,
      language: contentLanguage,
    },
    {
      enabled: !!productId,
      select: data => data.contentProduct,
      ...options,
    },
  )
}

export function useProductDataQuery(isEnabled = true) {
  const locale = useLocale()
  const productId = useProductId()
  const contentLanguage = mapLocaleToContentLanguage.get(locale) || ContentLanguage.LanguageEn

  if (!productId) {
    throw new Error('Product id is required')
  }

  return useContentFetchProductQuery(
    {
      id: productId,
      language: contentLanguage,
    },
    {
      enabled: !!productId && isEnabled,
      select: data => data.contentProduct,
    },
  )
}

export function useIsFirstDownload(isEnabled: boolean) {
  const productId = useProductId()
  const {
    data: isAlreadyDownloaded,
    isPending: isLoading,
    refetch,
  } = useContentFetchLicenseQuery(
    {
      input: {
        productID: productId ?? '',
      },
    },
    {
      select: data => Boolean(data.contentFetchLicense?.key),
      enabled: isEnabled,
    },
  )
  return {
    isLoading: isEnabled && isLoading,
    isFirstDownload: !isLoading && !isAlreadyDownloaded,
    refetch,
  }
}

export function useIsFirstLegacyProductDownload(productId: number, isEnabled: boolean) {
  const {
    data: isAlreadyDownloaded,
    isPending: isLoading,
    refetch,
  } = useLegacyProductsFetchLicenseQuery(
    {
      productId,
    },
    {
      select: data => Boolean(data.legacyProductsFetchLicense?.ID),
      enabled: isEnabled,
    },
  )
  return {
    isLoading: isEnabled && isLoading,
    isFirstDownload: !isLoading && !isAlreadyDownloaded,
    refetch,
  }
}
